import React from 'react';

import PropTypes from 'prop-types';

import {
    Form,
    FormError,
    FormInput,
    Validate,
} from 'wanderu-component-form';
import A from 'wanderu-component-react-router-link';
import SocialButtons from 'wanderu-component-social-buttons';
import {
    Button,
} from 'wanderu-components';
import {
    localize,
} from 'wanderu-i18n';
import { SnowplowService } from 'wanderu-service-analytics';

import baseAppConfig from 'wanderu-shellapp/src/config/baseApp.locales';

// eslint-disable-next-line css-modules/no-unused-class
import styles from './LogIn.scss';

class LogIn extends React.Component {
    static propTypes = {
        className: PropTypes.string,
        cognitoLoginFailed: PropTypes.bool,
        defaultValues: PropTypes.object,
        hasError: PropTypes.bool,
        loginFetching: PropTypes.bool,
        onFacebookClick: PropTypes.func.isRequired,
        onFormFocus: PropTypes.func,
        onGoogleClick: PropTypes.func.isRequired,
        onLogIn: PropTypes.func,
        showCreateAccount: PropTypes.bool,
        showForgotPassword: PropTypes.bool,
        t: PropTypes.func.isRequired,
        title: PropTypes.node,
        userHasThirdPartyAccount: PropTypes.bool,
    };

    static defaultProps = {
        className: '',
        cognitoLoginFailed: false,
        defaultValues: {},
        hasError: false,
        loginFetching: false,
        onFormFocus() {},
        showCreateAccount: true,
        showForgotPassword: true,
        title: null,
        userHasThirdPartyAccount: false,
    };

    constructor(props) {
        super(props);

        this.state = {
            emailChanged: false,
            formData: this.props.defaultValues,
        };
    }

    onFormFocus = () => {
        this.props.onFormFocus();
    }

    onFormSubmit = () => {
        this.setState(() => ({
            emailChanged: false,
        }));
    };

    onEmailChange = () => {
        this.setState(() => ({
            emailChanged: true,
        }));
    };

    onFormChange = (formData) => {
        this.setState(() => ({ formData }));
    };

    onSuccess = (formData) => {
        const { email, password } = formData;
        this.props.onLogIn(email, password);
    };

    renderUserHasThirdPartyAccountError() {
        const { t } = this.props;

        return (
            <FormError key="userHasThirdPartyAccount">
                <span>{t('login-with-third-party')}</span>
            </FormError>
        );
    }

    renderCognitoLoginFailedError() {
        const { t } = this.props;

        // TS: Do we want to remove the valid styling on the form in this case?
        return (
            <FormError key="authFailureError">
                <span>{t('auth-failure')}</span>
            </FormError>
        );
    }

    renderSignUpComponent() {
        const { t } = this.props;

        return (
            <div className={styles.signUp}>
                {t('dont-have-account')}
                &nbsp;
                <A
                    href="/account/signup/"
                    queries={{ email: this.state.formData.email }}
                >
                    {t('sign-up-button')}
                </A>
            </div>
        );
    }

    renderForgotPasswordComponent() {
        const { t } = this.props;

        return (
            <div
                className={styles.forgotPassword}
            >
                {t('forgot')}
                &nbsp;
                <A
                    href="/account/resetpassword/"
                    onClick={() => SnowplowService.fireStructuredEvent({
                        action: 'click',
                        category: 'user_accounts',
                        label: 'forgot_password_button',
                    })}
                    queries={{ email: this.state.formData.email }}>
                    {t('forgot-password-link')}
                </A>
            </div>
        );
    }

    get showForgotPassword() {
        const {
            showForgotPassword,
            userHasThirdPartyAccount,
        } = this.props;

        const {
            emailChanged,
        } = this.state;

        if (!showForgotPassword) {
            return false;
        }

        if (emailChanged) {
            return true;
        }

        return !userHasThirdPartyAccount;
    }

    render() {
        const {
            className,
            defaultValues,
            hasError,
            onFacebookClick,
            onGoogleClick,
            showCreateAccount,
            userHasThirdPartyAccount,
            cognitoLoginFailed,
            title,
            t,
        } = this.props;

        return (
            <div className={`${styles.container} ${className}`} data-testid="wanderu-component-account-login" >
                {title}
                <SocialButtons
                    onFacebookClick={onFacebookClick}
                    onGoogleClick={onGoogleClick}
                />
                <div className={styles.orContainer}>
                    <span className={styles.orBar} />
                    <span className={styles.or}>{t('or')}</span>
                    <span className={styles.orBar} />
                </div>

                {cognitoLoginFailed && this.renderCognitoLoginFailedError()}
                {userHasThirdPartyAccount && this.renderUserHasThirdPartyAccountError()}

                <Form
                    defaultValues={defaultValues}
                    hasError={hasError}
                    onChange={this.onFormChange}
                    onFocus={this.onFormFocus}
                    onSubmit={this.onFormSubmit}
                    onSuccess={this.onSuccess}
                >
                    <Validate
                        key="email"
                        type="email"
                        required
                    >
                        <FormInput
                            onKeyDown={this.onEmailChange}
                            placeholder={t('email')}
                            type="email"
                            autoFocus
                            clearable
                        />
                    </Validate>
                    <Validate
                        key="password"
                        type="login"
                        required
                    >
                        <FormInput
                            placeholder={t('password')}
                            type="password"
                        />
                    </Validate>

                    {this.showForgotPassword && this.renderForgotPasswordComponent()}

                    <Button
                        className={styles.loginButton}
                        loading={this.props.loginFetching}
                    >
                        {t('log-in')}
                    </Button>
                </Form>

                {showCreateAccount && this.renderSignUpComponent()}
            </div>
        );
    }
}

export { LogIn };
export default localize(LogIn, ['t'], baseAppConfig.namespace);
